import shouldForwardProp from '@styled-system/should-forward-prop'
import { Box as NormalBox } from 'mds-web-ui'
import React, { forwardRef } from 'react'
import styled from 'styled-components'
import {
  alignItems,
  borderRadius,
  color,
  display,
  flexbox,
  flexDirection,
  grid,
  height,
  justifyContent,
  layout,
  maxWidth,
  minHeight,
  overflow,
  position,
  space,
  system,
  typography,
  width
} from 'styled-system'

const Box = styled(NormalBox).withConfig({
  shouldForwardProp
})`
  display: flex;
  position: relative;

  ${flexDirection};
  ${height};
  ${minHeight};
  ${width};
  ${maxWidth};
  ${justifyContent};
  ${alignItems};
  ${color};
`
export const Container = forwardRef(({ children, bg, ...props }, ref) => {
  return (
    <Box
      px={[3, 5]}
      pt={[5, 6]}
      pb={[5, 6]}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      as="section"
      minHeight={['auto', '100vh']}
      width="100%"
      bg={bg}
      {...props}
      ref={ref}
    >
      {children}
    </Box>
  )
})

const scrollRevealSizes = {
  extraSmall: '50vh',
  small: ['100vh', '80vh'],
  medium: '98vh',
  large: '110vh',
  extraLarge: ['130vh', '130vh', '140vh', '130vh', '120vh']
}

export const ScrollRevealFlex = forwardRef(
  ({ size = 'medium', children, ...props }, ref) => (
    <ScrollReveal
      height={scrollRevealSizes?.[size] || size}
      width="100%"
      {...props}
      ref={ref}
      data-scrollreveal
    >
      {children}
    </ScrollReveal>
  )
)

const ScrollReveal = styled('div').withConfig({
  shouldForwardProp
})(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    position: 'relative'
  },
  flexbox,
  layout,
  space
)

export const Grid = styled('div').withConfig({
  shouldForwardProp
})(
  {
    display: 'grid'
  },
  props => ({
    placeItems: props.placeItems || 'center'
  }),
  props => props.css,
  space,
  layout,
  color,
  grid,
  position,
  flexbox,
  borderRadius,
  overflow
)

export const gap = system({
  gap: {
    property: 'gap',
    scale: 'space'
  }
})

export const GridFlex = styled('div')
  .withConfig({ shouldForwardProp })
  .attrs(props => ({
    ...props,
    display: props.display || 'flex',
    flex: props.flex || 'none'
  }))(
  {},
  props => props.css,
  space,
  layout,
  color,
  flexbox,
  grid,
  position,
  gap,
  typography,
  borderRadius,
  display,
  overflow
)

export const PositionBox = styled('div').withConfig({ shouldForwardProp })(
  {},
  props => props.css,
  space,
  layout,
  color,
  flexbox,
  grid,
  position
)

export const TransformBox = styled('div').withConfig({ shouldForwardProp })(
  {
    position: 'absolute',
    inset: 0,
    width: 'auto',
    height: '100%',
    transformStyle: 'preserve-3d',
    borderRadius: 'inherit'
  },
  layout,
  space,
  position
)

export const GpuAvailableBox = ({ children, ...props }) => (
  <PositionBox
    position={['relative', 'absolute']}
    width={['100%', '30vw']}
    maxWidth={['initial', 400]}
    {...props}
  >
    {children}
  </PositionBox>
)

export const Hide = ({ xs, s, m, l, children, ...props }) => {
  return (
    <GridFlex
      display={[
        xs ? 'none' : 'flex',
        s ? 'none' : 'flex',
        m ? 'none' : 'flex',
        l ? 'none' : 'flex'
      ]}
      {...props}
    >
      {children}
    </GridFlex>
  )
}
